import { computed, defineComponent, ref } from 'vue';
import Centered from 'components/centered';
import * as fns from 'date-fns';
import { useMemoAsync } from '@/utils/async/useMemoAsync';
import { EAssetType, type IntegrationProvider, type IProviderImport } from 'findef';
import { FindexClient } from '@/integrations/findex';
import { parseDate } from '@/utils/formatting/date';
import { Spinner } from 'components/spinner';
import TinyBadge from 'components/tiny-badge';
import Button from 'components/button';
import LinkIcon from 'assets/img/svg/icons/chain-link.svg?url';
import CogIcon from 'assets/img/svg/icons/cog_outline.svg?url';
import EmptyTableState, {
  EEmptyTableStateType,
} from 'components/empty-table-state';

import { popModal } from 'components/modal/store';
import type { DataTableRow } from 'components/data-table/types';
import { DataTable } from 'components/data-table';
import { useDataTable } from 'components/data-table/hooks/useDataTable';
import { useDataTableState } from 'components/data-table/hooks/useDataTableState';

export const ProviderImportTable = defineComponent<{
  handleSelectImport: (imp: IProviderImport) => any;
}>(
  (props) => {
    const { push } = useRouter();
    const isLoading = ref(false);
    const userImports = useMemoAsync<IProviderImport[]>(async () => {
      isLoading.value = true;
      const [imports] = await FindexClient.getProviderImports();
      console.log('FETCHED IMPORTS', imports);
      isLoading.value = false;
      return imports || [];
    });

    const providers = computed(() => userImports.data);

    const totalConnectedCount = computed(() => {
      if (!providers.value) return 0;
      return providers.value.filter((it) => !!it.provider).filter((it) => !!it.session?.connected)
        .length;
    });

    const totalCount = computed(() => {
      if (!providers.value) return 0;
      return providers.value.filter((it) => !!it.provider).length;
    });

    const handleClickReconnect = (providerImport: IProviderImport) => {
      props.handleSelectImport(providerImport as unknown as any);
      //props.finsquid.setProviderImport(providerImport as unknown as any)
      //props.handler.selectProvider(providerImport.providerId)
    };

    const rows = computed((): DataTableRow[] => {
      if (!providers.value) return [];
      return providers.value
        .filter((it) => !!it.provider)
        .map((imp): DataTableRow => {
          const provider = imp.provider as IntegrationProvider;
          const lastSynced = imp.lastSync
            ? fns.format(parseDate(imp.lastSync), 'd MMM, yyyy')
            : 'Never';

          return {
            columns: [
              {
                label: 'Provider',
                body: () => {
                  return (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'max-content auto',
                        gap: '1rem',
                      }}
                      class={'text-gray-900'}
                    >
                      <Centered>
                        {provider.iconUrl && (
                          <img
                            style={{
                              display: 'block',
                              width: '2rem',
                              aspectRatio: '1 / 1',
                              objectFit: 'contain',
                            }}
                            src={provider.iconUrl}
                          />
                        )}
                      </Centered>
                      <Centered>{provider.displayName || provider.name || '?'}</Centered>
                    </div>
                  );
                },
              },
              {
                label: 'Connection Status',
                body: () => {
                  if (isLoading.value) return <Spinner />;
                  const text = imp.session.connected ? 'Connected' : 'Session Expired';
                  const variant = imp.session.connected ? 'blue' : 'error';
                  return <TinyBadge variant={variant}>{text}</TinyBadge>;
                },
              },
              {
                label: 'Included',
                body: () => {
                  const selected = imp.selected.investmentIds;
                  const selectedCount = selected.length;
                  const avail = imp.available.bankAccounts
                    .map((acc) => acc.investments.map((inv) => inv.asset.externalId))
                    .flat();
                  const availCount = avail.length;
                  const numAccounts = imp.available.bankAccounts.length;
                  return (
                    <div class="space-y-1">
                      <div class="font-semibold text-gray-900">
                        {selectedCount} of {availCount} Assets
                      </div>
                      <div class="text-gray-500">From {numAccounts} accounts</div>
                    </div>
                  );
                },
              },
              {
                label: 'Last Data Sync',
                body: () => {
                  return <span class="text-gray-900">{lastSynced}</span>;
                },
              },
              {
                label: 'Action',
                body: () => {
                  return (
                    <Button
                      icon={{
                        src: LinkIcon,
                        stroke: 'currentColor',
                        size: 16,
                      }}
                      onClick={() => handleClickReconnect(imp)}
                    >
                      Reconnect
                    </Button>
                  );
                },
              },
            ],
          };
        });
    });

    const tableState = useDataTableState({});

    const table = useDataTable({
      rows: rows,
      state: tableState,
    });

    return () => {
      if (isLoading.value)
        return (
          <Centered>
            <Spinner />
          </Centered>
        );

      return (
        <div class="w-full h-full">
          <DataTable
            banner={{
              header: {
                title: 'Providers',
                subtitle: `${totalConnectedCount.value || 0} of ${totalCount.value || 0
                  } providers connected`,
              },
              actions: [
                {
                  body: () => {
                    return (
                      <Button
                        variant="transparent"
                        outlined
                        icon={{
                          src: CogIcon,
                          fill: 'currentColor',
                          size: 16,
                        }}
                        onClick={() => {
                          popModal();
                          push('/settings/integrations');
                        }}
                      >
                        Providers & Integrations
                      </Button>
                    );
                  },
                },
              ],
            }}
            table={table}
            border
            variant="gray"
            rounded
            emptyState={() => (
              <EmptyTableState
                assetType={EAssetType.UNDEFINED}
                type={EEmptyTableStateType.NO_PROVIDERS}
              />
            )}
          />
        </div>
      );
    };
  },
  { props: ['handleSelectImport'] },
);
